import { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Tooltip } from '@nike/eds';
import { useDebouncedCallback } from 'use-debounce';
import { Product, ProductGroup } from 'types/newproductwall';
import CardContent from './CardContent';

type ProductWallItemProps = {
  data: ProductGroup;
};

const mapProductItemHelper = (product: Product) => {
  const dc = product.displayColors;
  const simpleLabel = dc?.simpleColor?.label || '';
  const colorDesc = dc?.colorDescription || '';
  const colorNames = [simpleLabel, colorDesc].filter(Boolean).join('/');
  const uniqueDisplayColors = dc?.simpleColor?.hex ? [dc.simpleColor.hex] : [];
  return {
    colorNames,
    uniqueDisplayColors,
    title: product.copy?.title ?? '',
    subtitle: product.copy?.subTitle ?? '',
    colorDescription: product.displayColors?.colorDescription ?? '',
    styleColor: product.productCode ?? '',
    pbid: '',
    imageUrl:
      product.colorwayImages?.squarishURL ??
      product.colorwayImages?.portraitURL ??
      '',
    url: product.pdpUrl.url,
    availability: !product.featuredAttributes?.includes('OUT_OF_STOCK'),
    currency: product.prices?.currency ?? '',
    currentPrice: product.prices?.currentPrice ?? 0,
    fullPrice: product.prices?.initialPrice ?? 0,
    baseStyleColor: product.customization?.baseProductCode ?? '',
  };
};

export default function ProductWallItem(props: ProductWallItemProps) {
  const [visibleProductIdx, setVisibleProductIdx] = useState(0);
  const { data } = props;

  // Call hooks unconditionally before any returns
  const rollupProducts = useMemo(() => {
    return data.products?.map(mapProductItemHelper) || [];
  }, [data]);

  const handleMouseEnter = useDebouncedCallback((idx: number) => {
    if (rollupProducts.length < 2 || visibleProductIdx === idx) return;
    setVisibleProductIdx(idx);
  }, 100);

  // Check if this is an in-wall content card
  if (data.cardType === 'in_wall_content') {
    return <CardContent data={data} />;
  }

  const strategy = data.gcProductExplain?.ranking;

  return (
    <div
      className={classnames(
        'tw-rounded tw-bg-white tw-w-60 tw-h-800 tw-relative tw-rounded-br-{1.25rem}',
        {
          'tw-opacity-50':
            !rollupProducts[visibleProductIdx]?.availability ||
            visibleProductIdx >= rollupProducts.length,
        },
      )}
      style={{ boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)' }}
    >
      <div className='tw-flex tw-justify-between tw-items-start tw-px-1'>
        <div className='tw-pt-1 tw-text-[10px] tw-font-bold'>
          <div
            className='tw-rounded-full tw-bg-gray-300 tw-text-center tw-bg-primary'
            style={{
              padding: '2px 4px',
              fontSize: '.7rem',
              lineHeight: 1,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {strategy?.position + 1}
          </div>
        </div>
        <div className=' tw-text-right tw-flex tw-flex-col'>
          {true && (
            <Tooltip
              bodySlot={
                <div className='tw-text-gray-300 tw-text-[9px] tw-w-20 tw-text-center'>
                  {/* {strategy.strategy.detail
                  ? strategy.strategy.detail.strategyId
                  : 'no strategyId'} */}
                  {strategy?.strategy?.detail?.strategyId ?? 'no strategyId'}
                </div>
              }
              isDark
              placement={'top'}
            >
              <div className='tw-text-[10px] tw-font-bold'>
                {strategy?.strategy.name}
              </div>
            </Tooltip>
          )}
          <div className='tw-text-[7px] tw-font-bold tw-text-zinc-600 tw-uppercase'>
            {data.gcProductExplain?.ranking.recall?.recallPath.join(', ') || ''}
          </div>
        </div>
      </div>
      <div className='tw-px-5 tw-pb-3'>
        {rollupProducts.map((pdt, idx) => {
          const key = `${pdt.styleColor || pdt.pbid}`;
          return (
            <div
              key={key}
              className={classnames({ 'tw-hidden': visibleProductIdx !== idx })}
            >
              <a href={pdt.url} target='_blank' rel='noreferrer'>
                <img
                  src={pdt.imageUrl as string}
                  className='rounded tw-object-fit'
                  alt='product'
                  style={{ width: 200, height: 200 }}
                />
              </a>
              {rollupProducts.length > 1 && (
                <div className='tw-mt-2'>
                  <div className='tw-flex tw-gap-1 tw-justify-start tw-flex-wrap'>
                    {rollupProducts.map((p, idx) => {
                      return (
                        <a
                          key={p.styleColor}
                          href={p.url}
                          target='_blank'
                          rel='noreferrer'
                          className='tw-block'
                          onMouseEnter={() => handleMouseEnter(idx)}
                        >
                          <div
                            className={classnames(
                              'tw-h-8 tw-w-8 tw-bg-center tw-bg-contain tw-bg-no-repeat tw-bg-slate-200',
                              {
                                'tw-border-2 tw-border-indigo-500/100':
                                  idx === visibleProductIdx,
                              },
                            )}
                            style={{ backgroundImage: `url(${p.imageUrl})` }}
                          ></div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className=''>
                <a
                  className='tw-font-bold'
                  href={pdt.url}
                  target='_blank'
                  rel='noreferrer'
                >
                  {pdt.title}
                </a>
                <p className='tw-font-bold tw-text-gray-500'>{pdt.subtitle}</p>
                <p className='tw-text-sm'>{pdt.styleColor}</p>
                {pdt.baseStyleColor && (
                  <p className='tw-text-sm'>{pdt.baseStyleColor}</p>
                )}
                <div>
                  <p className='tw-text-sm'>{pdt.colorNames}</p>
                  <div className='tw-flex tw-items-center tw-pl-1'>
                    {pdt.uniqueDisplayColors.map((color, index) => (
                      <div
                        key={index}
                        className='tw-w-4 tw-h-4 tw-rounded-full tw-mr-2'
                        style={{
                          backgroundColor: '#' + color,
                          boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.5)',
                        }}
                      ></div>
                    ))}
                  </div>
                </div>
                <p className='tw-text-sm'>
                  {pdt.currency}:{' '}
                  {pdt.fullPrice !== pdt.currentPrice ? (
                    <>
                      <del className='tw-text-gray-500'>{pdt.fullPrice}</del>{' '}
                      <span className='tw-text-black-500 tw-font-bold'>
                        {pdt.currentPrice}
                      </span>
                    </>
                  ) : (
                    <span className='tw-text-black-500 tw-font-bold'>
                      {pdt.currentPrice}
                    </span>
                  )}
                </p>
                {!pdt.availability && (
                  <p className='tw-text-sm tw-font-bold tw-text-red-500'>
                    Out of Stock
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
